import { useEffect, useMemo, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@argo/common/auth'
import Login from '@argo/common/pages/Login'
import Register, { VerifyEmail } from '@argo/common/pages/Register'
import Forgot, { ResetFromLink } from '@argo/common/pages/Forgot'
import { api } from './api'
import { SessionContext } from './session'
import Layout from './components/Layout'
import Balance from './pages/Balance'
import Shop from './pages/Shop'
import Profile from './pages/Profile'
import PolicyDetail from './pages/PolicyDetail'
import Claims from './pages/Claims'
import ClaimDetail from './pages/ClaimDetail'
import Queue from './pages/Queue'
import Products from './pages/Products'
import Report from './pages/Report'

export default function App() {
  const { ready, authed } = useAuth()

  if (!ready) {
    return (
      <div className="auth">
        <div className="auth-card" style={{ display: 'grid', gap: 12 }}>
          <div className="skeleton" style={{ height: 38, width: '55%' }} />
          <div className="skeleton" style={{ height: 14 }} />
          <div className="skeleton" style={{ height: 14, width: '70%' }} />
        </div>
      </div>
    )
  }
  if (!authed) return <SignedOut />
  return <Session />
}

/**
 * Signed out: sign in, create an account, and the pages the emailed links land
 * on. The same screens the exchange shows, from the shared library — one
 * account opens both — so a customer who registers here can sign in there.
 */
const HOME = import.meta.env.BASE_URL || '/'

function readRoute() {
  return {
    path: window.location.pathname,
    token: new URLSearchParams(window.location.search).get('token'),
  }
}

function SignedOut() {
  const [route, setRoute] = useState(readRoute)
  const [view, setView] = useState<'in' | 'up' | 'forgot'>('in')

  useEffect(() => {
    const onPop = () => { setRoute(readRoute()); setView('in') }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const home = () => {
    window.history.replaceState(null, '', HOME)
    setRoute({ path: HOME, token: null })
    setView('in')
  }

  if (route.path.endsWith('/verify-email') && route.token)
    return <VerifyEmail token={route.token} onDone={home} />
  if (route.path.endsWith('/reset-password') && route.token)
    return <ResetFromLink token={route.token} onDone={home} />

  if (view === 'up') return <Register onDone={home} onCancel={() => setView('in')} />
  if (view === 'forgot') return <Forgot onDone={home} />
  return <Login onRegister={() => setView('up')} onForgot={() => setView('forgot')} />
}

function Session() {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 60_000 })
  const session = useMemo(() => ({ account: me.data?.account ?? '', me: me.data }), [me.data])

  return (
    <SessionContext.Provider value={session}>
      <Layout>
        <Routes>
          <Route path="/" element={<Balance />} />
          <Route path="/products" element={<Shop />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/policies/:id" element={<PolicyDetail />} />
          <Route path="/claims" element={<Claims />} />
          <Route path="/claims/:id" element={<ClaimDetail />} />
          <Route path="/insurer/claims" element={<Queue />} />
          <Route path="/insurer/products" element={<Products />} />
          <Route path="/insurer/report" element={<Report />} />
          <Route path="/settings" element={<Navigate to="/profile" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </SessionContext.Provider>
  )
}
